/**
 * FILE: apps\hub\src\hooks\useCourseAttempts.ts
 * PURPOSE: Custom React hook.
 * AGENT_CONTEXT: Reusable logic. Follow Rules of Hooks. No side effects in render.
 * LAST_REVIEWED: 2026-05-07
 */

import { useQuery } from '@tanstack/react-query'
import { fetchAttemptsForCourse } from '@fintree/supabase/course-attempts'
import {
  ATTEMPTS_BY_COURSE,
  canonicalCourseForStorage,
  filterAttemptsForUiDropdown,
  mergeAttemptOptions,
} from '@fintree/utils'
import { QK } from '@/lib/queryKeys'

const STALE_MS = 5 * 60_000

export function useCourseAttempts(
  course: string | null | undefined,
  opts?: { includeInactive?: boolean; uiDropdown?: boolean },
) {
  const courseKey = canonicalCourseForStorage(course?.trim() ?? '')

  const query = useQuery({
    queryKey: QK.courseAttempts(courseKey, opts?.includeInactive ?? false),
    queryFn: () => fetchAttemptsForCourse(courseKey, opts),
    enabled: Boolean(courseKey),
    staleTime: STALE_MS,
  })

  const fallback = courseKey ? (ATTEMPTS_BY_COURSE[courseKey] ?? []) : []
  let registryAttempts = (query.data ?? []).map((row) => row.attempt)
  if (opts?.uiDropdown !== false) {
    registryAttempts = filterAttemptsForUiDropdown(registryAttempts)
  }
  const attemptOptions = mergeAttemptOptions(
    registryAttempts,
    filterAttemptsForUiDropdown(fallback),
  )

  return {
    ...query,
    attemptOptions,
    registryAttempts,
    fallbackAttempts: fallback,
  }
}
